import { Card, CardContent, styled, Typography } from '@mui/material';
import Column from 'components/Layout/Column';
import Row from 'components/Layout/Row';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import OverviewRow from './OverviewRow';
import { CurrencyCode } from 'further-types/lp/vehicle';
import CounterpartyBreakdown from './CounterpartyBreakdown';
import { type TransactionOverview } from '../useCapitalActionNotice/types';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';

const labelsMap: Record<CapitalActionEntryType, string> = {
  [CapitalActionEntryType.Expense]: 'Capital call - Expense',
  [CapitalActionEntryType.Investment]: 'Capital call - Investment',
  [CapitalActionEntryType.Fees]: 'Capital call - Fees',
  [CapitalActionEntryType.GeneralCapitalCall]: 'Capital call - General capital',
  [CapitalActionEntryType.AdvanceSubscriptionPayment]:
    'Capital call - Advance subscription payment',
  [CapitalActionEntryType.CapitalContributionCall]:
    'Capital call - Capital contribution call',
  [CapitalActionEntryType.CapitalRefund]: 'Capital refund',
  [CapitalActionEntryType.CapitalDistribution]: 'Capital distribution',
  [CapitalActionEntryType.Equalisation]: 'Equalisation',
};

const StyledCard = styled(Card)(() => ({
  maxWidth: '550px',
}));

type Props = {
  currencyLcy: CurrencyCode;
  data: TransactionOverview;
};

const TransactionOverview: React.FC<Props> = ({ currencyLcy, data }) => {
  return (
    <StyledCard variant="outlined">
      <CardContent>
        <Column spacing="xl">
          <Typography variant="h5">
            Capital action transaction overview
          </Typography>
          <Column spacing="sm">
            {data.transactionEntries.map((entry) => (
              <OverviewRow
                key={entry.id}
                value={entry.totalAmountLcy}
                title={`${labelsMap[entry.entryType]} (${
                  entry.counterparties.length
                } ${pluralize('transaction', entry.counterparties.length)})`}
                currencyLcy={currencyLcy}
              >
                {data.transactionEntries.length > 1 ? (
                  <CounterpartyBreakdown
                    data={entry.counterparties.map((counterparty) => ({
                      label: counterparty.counterpartyLabel,
                      value: counterparty.amountLcy,
                    }))}
                    currencyLcy={currencyLcy}
                  />
                ) : null}
              </OverviewRow>
            ))}
            <Row justify="space-between" fullWidth>
              <Typography variant="h6">
                <b>Total to be called / (distributed)</b>
              </Typography>
              <Typography variant="h6">
                <b>
                  {numberToCurrencyString(data.totalCapitalCalledLcy, {
                    currency: currencyLcy,
                  })}
                </b>
              </Typography>
            </Row>
          </Column>
        </Column>
      </CardContent>
    </StyledCard>
  );
};

export default TransactionOverview;
